import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TableContainer,
  Grid,
} from "@mui/material";
import {
  LocalGasStation as LocalGasStationIcon,
  Add as AddIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import { API_URL } from "../../utils/api-config";
import FuelCardAddTransaction from "./FuelCardAddTransaction";

interface FuelCardDetailsProps {
  cardNumber: string;
}

interface ApiData {
  solde: number;
  transactions: { date: string; montant: number; station: string }[];
}

interface CardData {
  _id: string;
  cardNumber: string;
  provider: string;
  type: string;
  expiryDate: string;
  isActive: boolean;
  isConnected: boolean;
  externalId?: string;
  apiData?: ApiData;
}

interface Transaction {
  _id: string;
  date: string;
  amount: number;
  liters: number;
  fuelType: string;
  location: string;
  mileage: number;
  vehiclePlate?: string;
  driverId?: string;
}

interface Stats {
  totalAmount: number;
  totalLiters: number;
  averagePrice: number;
  transactionCount: number;
  lastTransaction?: string;
  monthlyBudget?: number;
  availableBalance?: number;
  budgetUsagePercent?: number;
}

export default function FuelCardDetails({ cardNumber }: FuelCardDetailsProps) {
  const [card, setCard] = useState<CardData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddTransaction, setShowAddTransaction] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/fuel-cards/${cardNumber}/connected`
        );
        if (!res.ok) throw new Error("Carte non trouvée ou non connectée");
        const data = await res.json();
        setCard(data);

        // Récupérer les transactions détaillées
        try {
          setLoadingDetails(true);
          const transRes = await fetch(
            `${API_URL}/fuel-cards/${cardNumber}/transactions`
          );
          if (transRes.ok) {
            const transData = await transRes.json();
            // Vérifier si la réponse contient un tableau 'transactions'
            if (transData.transactions && Array.isArray(transData.transactions)) {
              setTransactions(transData.transactions);
            } else if (Array.isArray(transData)) {
              setTransactions(transData);
            } else {
              setTransactions([]);
            }
          }

          // Récupérer les statistiques
          const statsRes = await fetch(
            `${API_URL}/fuel-cards/${cardNumber}/stats`
          );
          if (statsRes.ok) {
            const statsData = await statsRes.json();
            console.log("✅ Nouvelles statistiques récupérées:", statsData);

            // Mise à jour des statistiques en tenant compte de formats potentiellement différents
            if (
              statsData.totalSpent !== undefined &&
              statsData.totalAmount === undefined
            ) {
              // Adapter le format si nécessaire
              setStats({
                totalAmount: statsData.totalSpent,
                totalLiters: statsData.totalLiters || 0,
                averagePrice: statsData.averagePrice || 0,
                transactionCount: statsData.totalTransactions || 0,
                monthlyBudget: statsData.monthlyBudget,
                availableBalance: statsData.availableBalance,
                budgetUsagePercent: statsData.budgetUsagePercent,
                lastTransaction: statsData.lastTransaction,
              });
            } else {
              setStats(statsData);
            }
          }
        } catch (detailsErr) {
          console.error(
            "Erreur lors du chargement des détails supplémentaires",
            detailsErr
          );
        } finally {
          setLoadingDetails(false);
        }
      } catch {
        setError("Impossible de charger les détails de la carte");
      } finally {
        setLoading(false);
      }
    };
    if (cardNumber) fetchDetails();
  }, [cardNumber]);

  const toggleAddTransaction = () => setShowAddTransaction(!showAddTransaction);

  const handleAddTransactionSuccess = () => {
    console.log(
      "🔄 Rafraîchissement des données après ajout d'une transaction"
    );
    const fetchUpdatedData = async () => {
      try {
        setLoadingDetails(true);

        // Récupérer les transactions mises à jour
        console.log(
          "🔹 Récupération des transactions mises à jour pour la carte:",
          cardNumber
        );
        const transRes = await fetch(
          `${API_URL}/fuel-cards/${cardNumber}/transactions`
        );
        if (transRes.ok) {
          const transData = await transRes.json();
          console.log("✅ Nouvelles transactions récupérées:", transData);

          // Vérifier si la réponse contient un tableau 'transactions'
          if (transData.transactions && Array.isArray(transData.transactions)) {
            setTransactions(transData.transactions);
          } else if (Array.isArray(transData)) {
            setTransactions(transData);
          } else {
            setTransactions([]);
          }
        } else {
          console.error(
            "❌ Erreur lors de la récupération des transactions:",
            await transRes.text()
          );
        }

        // Récupérer les statistiques mises à jour
        console.log(
          "🔹 Récupération des statistiques mises à jour pour la carte:",
          cardNumber
        );
        const statsRes = await fetch(
          `${API_URL}/fuel-cards/${cardNumber}/stats`
        );
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          console.log("✅ Nouvelles statistiques récupérées:", statsData);

          // Mise à jour des statistiques en tenant compte de formats potentiellement différents
          if (
            statsData.totalSpent !== undefined &&
            statsData.totalAmount === undefined
          ) {
            // Adapter le format si nécessaire
            setStats({
              totalAmount: statsData.totalSpent,
              totalLiters: statsData.totalLiters || 0,
              averagePrice: statsData.averagePrice || 0,
              transactionCount: statsData.totalTransactions || 0,
              monthlyBudget: statsData.monthlyBudget,
              availableBalance: statsData.availableBalance,
              budgetUsagePercent: statsData.budgetUsagePercent,
              lastTransaction: statsData.lastTransaction,
            });
          } else {
            setStats(statsData);
          }
        } else {
          console.error(
            "❌ Erreur lors de la récupération des statistiques:",
            await statsRes.text()
          );
        }
      } catch (detailsErr) {
        console.error(
          "❌ Erreur lors du rafraîchissement des données:",
          detailsErr
        );
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchUpdatedData();
  };

  if (loading)
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Typography color="error">{error}</Typography>;
  if (!card) return <Typography>Aucune donnée à afficher.</Typography>;

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1, width: "100%" }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, display: "flex", alignItems: "center", fontWeight: 600 }}
      >
        <LocalGasStationIcon sx={{ mr: 1 }} />
        Détail de la carte
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
          <Typography>
            <strong>Numéro :</strong> {card.cardNumber}
          </Typography>
          <Typography>
            <strong>Fournisseur :</strong> {card.provider}
          </Typography>
          <Typography>
            <strong>Type :</strong> {card.type}
          </Typography>
          <Typography>
            <strong>Expiration :</strong>{" "}
            {new Date(card.expiryDate).toLocaleDateString("fr-FR")}
          </Typography>
        </Grid>
        <Grid sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
          <Typography component="div">
            <strong>Statut :</strong>{" "}
            <Chip
              label={card.isActive ? "Active" : "Inactive"}
              color={card.isActive ? "success" : "default"}
              size="small"
            />
          </Typography>
          <Typography component="div">
            <strong>Connectée :</strong>{" "}
            <Chip
              label={card.isConnected ? "Oui" : "Non"}
              color={card.isConnected ? "info" : "default"}
              size="small"
            />
          </Typography>
          {card.isConnected && card.apiData && (
            <Typography sx={{ mt: 1 }}>
              <strong>Solde disponible :</strong> {card.apiData.solde} €
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Statistiques */}
      {stats && (
        <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: "#f5f9ff", borderRadius: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, display: "flex", alignItems: "center" }}
          >
            <ShowChartIcon sx={{ mr: 1, fontSize: 20 }} />
            Statistiques de consommation
          </Typography>
          <Grid container spacing={2}>
            <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
              <Typography variant="body2" color="text.secondary">
                Total dépensé
              </Typography>
              <Typography variant="h6">
                {stats && typeof stats.totalAmount === "number"
                  ? stats.totalAmount.toFixed(2)
                  : "0.00"}{" "}
                €
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
              <Typography variant="body2" color="text.secondary">
                Litres totaux
              </Typography>
              <Typography variant="h6">
                {stats && typeof stats.totalLiters === "number"
                  ? stats.totalLiters.toFixed(1)
                  : "0.0"}{" "}
                L
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
              <Typography variant="body2" color="text.secondary">
                Prix moyen
              </Typography>
              <Typography variant="h6">
                {stats && typeof stats.averagePrice === "number"
                  ? stats.averagePrice.toFixed(2)
                  : "0.00"}{" "}
                €/L
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
              <Typography variant="body2" color="text.secondary">
                Transactions
              </Typography>
              <Typography variant="h6">
                {(stats && stats.transactionCount) || 0}
              </Typography>
            </Grid>

            {/* Affichage du budget si disponible */}
            {stats.monthlyBudget && (
              <>
                <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <Typography variant="body2" color="text.secondary">
                    Budget mensuel
                  </Typography>
                  <Typography variant="h6">
                    {stats.monthlyBudget.toFixed(2)} €
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <Typography variant="body2" color="text.secondary">
                    Solde disponible
                  </Typography>
                  <Typography
                    variant="h6"
                    color={
                      stats.availableBalance && stats.availableBalance < 50
                        ? "error"
                        : "inherit"
                    }
                  >
                    {stats.availableBalance?.toFixed(2) || 0} €
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Typography variant="body2" color="text.secondary">
                    Utilisation du budget
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 8,
                        bgcolor: "#e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.min(
                            stats.budgetUsagePercent || 0,
                            100
                          )}%`,
                          height: "100%",
                          bgcolor:
                            stats.budgetUsagePercent &&
                            stats.budgetUsagePercent > 90
                              ? "error.main"
                              : "primary.main",
                          transition: "width 0.5s ease-in-out",
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ ml: 1, minWidth: 45 }}>
                      {stats.budgetUsagePercent?.toFixed(1) || 0}%
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* Bouton Ajouter une transaction */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">Historique des transactions</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={toggleAddTransaction}
        >
          Ajouter une transaction
        </Button>
      </Box>

      {/* Tableau des transactions détaillées */}
      {loadingDetails ? (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <CircularProgress size={30} />
        </Box>
      ) : transactions.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell>Litres</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Prix/L</TableCell>
                <TableCell>Station</TableCell>
                <TableCell>Kilométrage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((t) => (
                <TableRow key={t._id}>
                  <TableCell>
                    {t.date
                      ? new Date(t.date).toLocaleDateString("fr-FR")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {typeof t.amount === "number"
                      ? t.amount.toFixed(2)
                      : "0.00"}{" "}
                    €
                  </TableCell>
                  <TableCell>{t.liters || 0} L</TableCell>
                  <TableCell>{t.fuelType || "Non spécifié"}</TableCell>
                  <TableCell>
                    {t.liters && t.amount
                      ? (t.amount / t.liters).toFixed(2)
                      : "0.00"}{" "}
                    €/L
                  </TableCell>
                  <TableCell>{t.location || "Non spécifié"}</TableCell>
                  <TableCell>
                    {t.mileage || "-"} {t.mileage ? "km" : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          sx={{ textAlign: "center", my: 2, color: "text.secondary" }}
        >
          Aucune transaction enregistrée pour cette carte
        </Typography>
      )}

      {/* Transactions API basiques */}
      {card.isConnected && card.apiData && Array.isArray(card.apiData.transactions) && card.apiData.transactions.length > 0 && !transactions.length && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Transactions simples (API externe) :
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Montant (€)</TableCell>
                <TableCell>Station</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {card.apiData.transactions.map((t, idx) => (
                <TableRow key={idx}>
                  <TableCell>{t.date}</TableCell>
                  <TableCell>{t.montant}</TableCell>
                  <TableCell>{t.station}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {!card.isConnected && (
        <Typography sx={{ mt: 2 }}>
          Cette carte n'est pas connectée à une API ou aucune donnée disponible.
        </Typography>
      )}

      {/* Formulaire d'ajout de transaction */}
      <FuelCardAddTransaction
        cardNumber={cardNumber}
        open={showAddTransaction}
        onClose={() => setShowAddTransaction(false)}
        onSuccess={handleAddTransactionSuccess}
      />
    </Paper>
  );
}
